import React, { useState, useEffect } from 'react';
import { Upload, FileText, Download, Trash2, CheckCircle2, AlertCircle, RefreshCw, FileCheck, FileEdit } from 'lucide-react';
import api from '../services/api';
import InAppTemplateEditorModal from './InAppTemplateEditorModal';
import { fetchDocumentTypes, documentTypeBothNames } from '../utils/documentTypes';
import './TemplateManagementModal.css';

const DEFAULT_TEMPLATE_TYPES = [
  { value: 'work_certificate', label: 'Work Certificate (Attestation de Travail)' },
  { value: 'salary_certificate', label: 'Salary Certificate (Attestation de Salaire)' },
  { value: 'employment_certificate', label: 'Employment Certificate (Attestation d\'Emploi)' },
  { value: 'resignation_certificate', label: 'Resignation Certificate (Attestation de Démission)' },
  { value: 'internship_certificate', label: 'Attestation de Stage (Internship Certificate)' },
];

const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];

const TemplateManagementModal = ({ isOpen, onClose }) => {
  const [selectedType, setSelectedType] = useState('work_certificate');
  const [language, setLanguage] = useState('fr');
  const [templateTypes, setTemplateTypes] = useState(DEFAULT_TEMPLATE_TYPES);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isDragging, setIsDragging] = useState(false);
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [showInAppEditorModal, setShowInAppEditorModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const [data, documentTypes] = await Promise.all([api.get('/attestations/templates'), fetchDocumentTypes()]);
      const templateMap = {};
      // Types still offered, under the names the admin gave them
      const customTypesFromApi = documentTypes
        .filter((t) => t.active)
        .map((t) => ({ value: t.type, label: documentTypeBothNames(t) }));
      const removedTypes = documentTypes.filter((t) => !t.active).map((t) => t.type);

      if (Array.isArray(data)) {
        data.forEach((t) => {
          const typeKey = t.attestationType || t.type;
          if (typeKey) {
            templateMap[typeKey] = {
              exists: true,
              filename: t.originalFileName || t.templateName || `${typeKey}.docx`,
              size: t.size || 6713,
              updatedAt: t.updatedAt || t.createdAt || new Date(),
              raw: t,
            };
            if (!customTypesFromApi.some(ct => ct.value === typeKey) && !removedTypes.includes(typeKey)) {
              const formattedLabel = typeKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              customTypesFromApi.push({ value: typeKey, label: `${formattedLabel} (Custom)` });
            }
          }
        });
      }
      setTemplateTypes(customTypesFromApi);
      setTemplates(templateMap);
      setSelectedType((current) => (customTypesFromApi.some((ct) => ct.value === current) || customTypesFromApi.length === 0
        ? current
        : customTypesFromApi[0].value));
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewType = async () => {
    if (!newTypeName.trim()) return;
    const typeValue = newTypeName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_');
    if (templateTypes.some(t => t.value === typeValue)) {
      setMessage({ type: 'error', text: 'This document type already exists!' });
      return;
    }

    try {
      await api.post('/attestations/templates/types/create', {
        type: typeValue,
        label: newTypeName.trim(),
      });

      const newTypeObj = {
        value: typeValue,
        label: `${newTypeName.trim()} (Custom)`,
      };

      setTemplateTypes(prev => [...prev, newTypeObj]);
      setSelectedType(typeValue);
      setShowAddTypeModal(false);
      setNewTypeName('');
      setMessage({ type: 'success', text: `New document type "${newTypeName}" added & saved! Upload a .docx template to activate it.` });
      fetchTemplates();
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Failed to save new custom type' });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    if (!file.name.endsWith('.docx')) {
      setMessage({ type: 'error', text: 'Please select a valid Word Document (.docx)' });
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    setMessage({ type: '', text: '' });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please choose a .docx file to upload' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', selectedType);

      await api.upload('POST', `/attestations/templates/${selectedType}?lang=${language}`, formData);
      setMessage({ type: 'success', text: `Template for "${templateTypes.find(t => t.value === selectedType)?.label}" (${language.toUpperCase()}) uploaded successfully! Its page size and margins were kept.` });
      setSelectedFile(null);
      fetchTemplates();
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Failed to upload template' });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (type) => {
    try {
      const blob = await api.downloadBlob(`/attestations/templates/${type}/download?lang=${language}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-template-${language}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to download template: ' + (err.message || '') });
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Reset the ${language === 'en' ? 'English' : 'French'} version to the built-in template?`)) return;
    try {
      await api.delete(`/attestations/templates/${type}?lang=${language}`);
      setMessage({ type: 'info', text: 'Template version reset to the built-in one.' });
      fetchTemplates();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete template' });
    }
  };

  if (!isOpen) return null;

  // Each document type has a French and an English version
  const rawTemplate = templates[selectedType]?.raw;
  const versionOf = (raw, lang) => ({
    fileName: lang === 'en' ? raw?.originalFileNameEn : raw?.originalFileName,
    customized: !!(lang === 'en' ? raw?.htmlContentEn : raw?.htmlContent),
  });
  const currentVersion = versionOf(rawTemplate, language);
  const languageLabel = LANGUAGES.find((l) => l.value === language)?.label;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content template-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-icon-title">
            <FileCheck size={24} className="header-icon" />
            <div>
              <h2>Document Templates Management</h2>
              <p>Each document has a French and an English version. Edit them in the app or upload a Word (.docx) file.</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {message.text && (
          <div className={`modal-message ${message.type}`}>
            {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
            <span>{message.text}</span>
          </div>
        )}

        <div className="template-modal-body">
          <div className="template-selector-section">
            <div className="section-title-with-btn">
              <label className="input-label">Select Attestation Document Type</label>
              <button
                type="button"
                className="btn btn-outline-primary btn-xs"
                onClick={() => setShowAddTypeModal(true)}
              >
                + Add Custom Certificate Type
              </button>
            </div>
            <div className="template-type-grid">
              {templateTypes.map((t) => {
                const raw = templates[t.value]?.raw;
                return (
                  <button
                    key={t.value}
                    type="button"
                    className={`template-type-btn ${selectedType === t.value ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedType(t.value);
                      setSelectedFile(null);
                      setMessage({ type: '', text: '' });
                    }}
                  >
                    <span className="type-name">{t.label}</span>
                    <span style={{ display: 'flex', gap: 4 }}>
                      {LANGUAGES.map((l) => {
                        const customized = versionOf(raw, l.value).customized;
                        return (
                          <span key={l.value} className={`status-pill ${customized ? 'active' : 'default'}`}>
                            {l.value.toUpperCase()} {customized ? 'custom' : 'standard'}
                          </span>
                        );
                      })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="section-title-with-btn" style={{ marginBottom: 12 }}>
            <label className="input-label">Language version</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {LANGUAGES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  className={`btn btn-xs ${language === l.value ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setLanguage(l.value);
                    setSelectedFile(null);
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="template-upload-container">
            <div className="template-status-card">
              <h3>{languageLabel} version</h3>
              {loading ? (
                <div className="template-loading"><RefreshCw className="spin" size={16} /> Loading template info...</div>
              ) : (
                <div className={currentVersion.customized ? 'template-info-box' : 'no-template-info'}>
                  {currentVersion.customized && <FileText size={32} className="docx-icon" />}
                  <div className="info-details">
                    <span className="file-name" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                      <span>
                        {currentVersion.fileName
                          || (currentVersion.customized ? 'Edited in the app' : 'Built-in template (not customized yet)')}
                      </span>
                      {rawTemplate?.reference && (
                        <span style={{ fontSize: '0.75rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                          Réf: {rawTemplate.reference}
                        </span>
                      )}
                    </span>
                    {currentVersion.customized && rawTemplate?.updatedAt && (
                      <span className="file-meta">
                        Last updated: {new Date(rawTemplate.updatedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="template-actions" style={{ marginTop: currentVersion.customized ? 0 : 12 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowInAppEditorModal(true)}>
                      <FileEdit size={14} /> Edit in App
                    </button>
                    {currentVersion.fileName && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(selectedType)}>
                        <Download size={14} /> Download
                      </button>
                    )}
                    {currentVersion.customized && (
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selectedType)}>
                        <Trash2 size={14} /> Reset
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="template-uploader">
              <h3>Upload / Replace the {languageLabel} Word file (.docx)</h3>
              <div
                className={`dropzone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload size={28} className="upload-icon" />
                {selectedFile ? (
                  <div className="selected-file-details">
                    <span className="name">{selectedFile.name}</span>
                    <span className="size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ) : (
                  <div>
                    <p className="drop-text">Drag and drop your Word (.docx) file here</p>
                    <p className="sub-text">or click to browse from computer</p>
                  </div>
                )}
                <input
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  className="file-input-hidden"
                />
              </div>

              <button
                className="btn btn-primary btn-upload"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? (
                  <><RefreshCw className="spin" size={16} /> Uploading Template...</>
                ) : (
                  <><Upload size={16} /> Save & Activate Template</>
                )}
              </button>
            </div>
          </div>

          <div className="placeholders-guide">
            <h4>How employee information is inserted</h4>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
              No codes to type. After uploading, click <strong>Edit in App</strong>, place the cursor where the
              information should appear and pick it from the list (name, CIN, position, dates...). The page size,
              margins and number of pages of your Word file are kept when the document is printed or exported to PDF.
            </p>
          </div>
        </div>
      </div>

      {showAddTypeModal && (
        <div className="modal-overlay submodal-overlay" onClick={() => setShowAddTypeModal(false)}>
          <div className="modal-content add-type-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Custom Certificate Type</h2>
              <button className="close-btn" onClick={() => setShowAddTypeModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label className="input-label">Certificate Title / Name *</label>
                <input
                  type="text"
                  className="form-control"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color, #cbd5e1)', marginTop: '6px' }}
                  placeholder="e.g. Internship Certificate, Recommendation Letter"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-actions" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: 'var(--bg-secondary, #f8fafc)' }}>
              <button className="btn btn-secondary" onClick={() => setShowAddTypeModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddNewType} disabled={!newTypeName.trim()}>Add Certificate</button>
            </div>
          </div>
        </div>
      )}

      {showInAppEditorModal && (
        <InAppTemplateEditorModal
          isOpen={showInAppEditorModal}
          onClose={() => setShowInAppEditorModal(false)}
          templateType={selectedType}
          templateName={templateTypes.find(t => t.value === selectedType)?.label || selectedType}
          initialLanguage={language}
          onSaveSuccess={() => {
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
};

export default TemplateManagementModal;
